import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import JSZip from 'jszip';
import { v4 as uuid } from 'uuid';
import {
  ArtifactStatus,
  CreateArtifactsRegistryInput,
  EntityTypeEnum,
  FileTypeEnum,
  GetEntityQuery,
  ModelArtifactsRegistryFilterInput,
  S3FileInput,
} from 'app/API.service';
import { CollectionService } from 'app/collection/collection.service';
import { CUSTOMAPIService } from 'app/custom-api.service';
import { EntityService } from 'app/shared/entity.service';
import { FileService } from 'app/shared/file.service';
import { UserService } from 'app/shared/user.service';

export type CollectionUploadArtifactSortOrder = 'latest' | 'oldest';

export interface CollectionUploadArtifactItem {
  id: string;
  name: string;
  uploadedBy: string;
  uploadedDate: string;
  createdBy: string;
  date: string;
  createdAt: string;
  creatorId?: string;
}

export interface CollectionUploadFileValidationOptions {
  requireDocx?: boolean;
  docxMimeType?: string;
  docxWarningMessage?: string;
}

export interface CollectionUploadFileValidationResult {
  valid: boolean;
  stopProcessing: boolean;
}

export interface CollectionUploadToRegistryResult {
  id: string;
  name: string;
  s3Key: string;
}

@Injectable({
  providedIn: 'root',
})
export class CollectionUploadArtifactPickerService {
  constructor(
    private customApi: CUSTOMAPIService,
    private collectionService: CollectionService,
    private entityService: EntityService,
    private fileService: FileService,
    private userService: UserService,
    private toastrService: ToastrService
  ) {}

  async fetchArtifacts(entityId: string, subEntityId = ''): Promise<CollectionUploadArtifactItem[]> {
    if (!entityId) {
      return [];
    }

    const allApiItems: any[] = [];
    let nextToken: string | null | undefined = null;
    const isParticipant = this.collectionService.isParticipant;
    const currentUser = this.collectionService.getCurrentUser();
    const currentRole = this.collectionService.currentUserRole?.name?.toLowerCase() ?? '';
    const isSubEntityLeader = currentRole === 'sub-entity leader';
    const subEntityFilter = this.getSubEntityFilter(isParticipant, isSubEntityLeader, currentUser, subEntityId);
    const filter: ModelArtifactsRegistryFilterInput = {
      parentID: { attributeExists: true },
      questionId: { attributeExists: false },
      ...subEntityFilter,
    };

    do {
      const response = await this.customApi.ArtifactsByEntityId(
        entityId,
        undefined,
        filter,
        500,
        nextToken || undefined
      );

      if (response?.items) {
        allApiItems.push(...response.items);
      }

      nextToken = response?.nextToken;
    } while (nextToken);

    return this.mapArtifacts(allApiItems, currentUser, isParticipant, isSubEntityLeader, subEntityId);
  }

  /**
   * Fetches all top-level artifacts for an entity (the same set shown in the Artifacts Registry).
   * Unlike fetchArtifacts, this does not require a sub-entity link, so it returns every
   * uploaded artifact in the entity's registry.
   */
  async fetchEntityTopLevelArtifacts(entityId: string): Promise<CollectionUploadArtifactItem[]> {
    if (!entityId) {
      return [];
    }

    const allApiItems: any[] = [];
    let nextToken: string | null | undefined = null;
    const filter: ModelArtifactsRegistryFilterInput = {
      parentID: { attributeExists: false },
      status: { ne: ArtifactStatus.ARCHIVED },
    };

    do {
      const response = await this.customApi.ArtifactsByEntityId(
        entityId,
        undefined,
        filter,
        500,
        nextToken || undefined
      );

      if (response?.items) {
        allApiItems.push(...response.items);
      }

      nextToken = response?.nextToken;
    } while (nextToken);

    return allApiItems.map(item => this.mapTopLevelArtifact(item));
  }

  /**
   * Fetches top-level registry artifacts linked to any of the given sub-entities. Top-level
   * artifacts don't carry a subEntityId themselves — the linkage lives on child rows
   * (parentID + subEntityId), so we collect the child rows' parentIDs and point-lookup
   * those parents (same approach as the Artifacts Registry's sub-entity leader view).
   */
  async fetchSubEntityTopLevelArtifacts(
    entityId: string,
    subEntityIds: string[]
  ): Promise<CollectionUploadArtifactItem[]> {
    if (!entityId || !subEntityIds.length) {
      return [];
    }

    const linkItems: any[] = [];
    let nextToken: string | null | undefined = null;
    const subEntityFilter: ModelArtifactsRegistryFilterInput =
      subEntityIds.length === 1
        ? { subEntityId: { eq: subEntityIds[0] } }
        : { or: subEntityIds.map(id => ({ subEntityId: { eq: id } })) };
    //* No status filter here: link rows are created without a status attribute and a
    //* DynamoDB ne-comparison excludes items missing the attribute — filter client-side.
    //* No questionId filter either: older artifacts are linked to a sub-entity only via
    //* question-link rows (questionId set) and have no plain tuple rows, so any child row
    //* with a matching subEntityId marks its parent as visible.
    const filter: ModelArtifactsRegistryFilterInput = {
      parentID: { attributeExists: true },
      ...subEntityFilter,
    };

    do {
      const response = await this.customApi.ArtifactsByEntityId(
        entityId,
        undefined,
        filter,
        500,
        nextToken || undefined
      );

      if (response?.items) {
        linkItems.push(...response.items);
      }

      nextToken = response?.nextToken;
    } while (nextToken);

    const parentIds = Array.from(
      new Set(
        linkItems
          .filter(item => item.status !== ArtifactStatus.ARCHIVED)
          .map(item => item.parentID)
          .filter(Boolean)
      )
    );
    const parents = await Promise.all(parentIds.map(id => this.customApi.GetArtifactsRegistry(id).catch(() => null)));

    return parents
      .filter(item => item && !item.parentID && item.status !== ArtifactStatus.ARCHIVED)
      .map(item => this.mapTopLevelArtifact(item));
  }

  private mapTopLevelArtifact(item: any): CollectionUploadArtifactItem {
    const creatorName = item.creator?.name || 'Unknown User';
    const formattedDate = this.formatArtifactDate(item.createdAt);
    return {
      id: item.id,
      name: item.name || '',
      uploadedBy: creatorName,
      uploadedDate: formattedDate,
      createdBy: creatorName,
      date: formattedDate,
      createdAt: item.createdAt || '',
      creatorId: item.creatorId,
    };
  }

  searchArtifacts<T extends { name: string }>(artifacts: T[], searchText: string): T[] {
    const normalizedSearchText = searchText.trim().toLowerCase();

    if (!normalizedSearchText) {
      return [...artifacts];
    }

    return artifacts.filter(artifact => artifact.name.toLowerCase().includes(normalizedSearchText));
  }

  sortArtifacts<T extends { createdAt?: string | number }>(
    artifacts: T[],
    sortOrder: CollectionUploadArtifactSortOrder
  ): T[] {
    // Decorate-sort-undecorate: parse each date once instead of on every comparison
    return artifacts
      .map(artifact => ({
        artifact,
        createdAtTime: artifact.createdAt ? new Date(artifact.createdAt).getTime() : 0,
      }))
      .sort((firstEntry, secondEntry) =>
        sortOrder === 'latest'
          ? secondEntry.createdAtTime - firstEntry.createdAtTime
          : firstEntry.createdAtTime - secondEntry.createdAtTime
      )
      .map(entry => entry.artifact);
  }

  filterAndSortArtifacts<T extends { name: string; createdAt?: string | number }>(
    artifacts: T[],
    searchText: string,
    sortOrder: CollectionUploadArtifactSortOrder
  ): T[] {
    return this.sortArtifacts(this.searchArtifacts(artifacts, searchText), sortOrder);
  }

  async validateArtifactFile(
    file: File,
    options: CollectionUploadFileValidationOptions = {}
  ): Promise<CollectionUploadFileValidationResult> {
    if (file.type === 'application/pdf' && (await this.fileService.scanPdfFile(file))) {
      this.toastrService.warning('Your File Contain Malicious Content');
      return { valid: false, stopProcessing: true };
    }

    if (options.requireDocx && options.docxMimeType && file.type !== options.docxMimeType) {
      this.toastrService.warning(options.docxWarningMessage || 'Only Docx file(s) can be uploaded on this question');
      return { valid: false, stopProcessing: true };
    }

    if (FileService.validateFileSize(file)) {
      this.toastrService.warning(file.name + ' Size can not be greater than 30MB');
      return { valid: false, stopProcessing: false };
    }

    if (!FileService.validateFileType(file, FileTypeEnum.ARTIFACTS)) {
      this.toastrService.warning(file.name + ' File type not supported');
      return { valid: false, stopProcessing: false };
    }

    return { valid: true, stopProcessing: false };
  }

  isValidArtifactFile(file: File): boolean {
    return !FileService.validateFileSize(file) && FileService.validateFileType(file, FileTypeEnum.ARTIFACTS);
  }

  async uploadFileToArtifactsRegistry(
    file: File,
    entityId: string,
    subEntityIds: string[] = []
  ): Promise<CollectionUploadToRegistryResult> {
    if (!entityId) {
      throw new Error('Entity ID not available for artifact upload.');
    }

    const currentUser = (await this.userService.getCurrentUser()) || this.collectionService.getCurrentUser();
    const creatorId = currentUser?.id;

    if (!creatorId) {
      throw new Error('User ID not available for artifact upload.');
    }

    const artifactId = uuid();
    const zip = new JSZip();
    zip.file(file.name, file);
    const zippedFile = await zip.generateAsync({ type: 'blob' });
    const s3FileInput: S3FileInput = {
      id: artifactId,
      name: file.name,
      entityId,
      fileType: FileTypeEnum.CLIENT_WIZARD_FILES,
      contentType: zippedFile.type || 'application/zip',
      body: zippedFile as any,
      date: Date.now(),
      url: `ARTIFACTS_REGISTRY/${entityId}/${artifactId}`,
    };
    const s3Response = await this.fileService.uploadToS3(s3FileInput, false);
    const s3Key = s3Response?.key || `public/ARTIFACTS_REGISTRY/${entityId}/${artifactId}`;
    const rootInput: CreateArtifactsRegistryInput = {
      id: artifactId,
      entityId,
      name: file.name,
      creatorId,
      tags: [],
      categories: [],
    };

    await this.customApi.CreateArtifactsRegistry(rootInput);

    const targetSubEntities = await this.resolveUploadSubEntities(entityId, subEntityIds);

    if (targetSubEntities.length > 0) {
      await Promise.all(
        targetSubEntities.map(subEntity => {
          const childInput: CreateArtifactsRegistryInput = {
            parentID: artifactId,
            entityId,
            subEntityId: subEntity.id,
            name: file.name,
            creatorId,
            ...(subEntity.activeAssessmentId ? { assessmentId: subEntity.activeAssessmentId } : {}),
            tags: [],
            categories: [],
          };
          return this.customApi.CreateArtifactsRegistry(childInput);
        })
      );
    }

    return {
      id: artifactId,
      name: file.name,
      s3Key,
    };
  }

  private async resolveUploadSubEntities(entityId: string, subEntityIds: string[]): Promise<GetEntityQuery[]> {
    if (subEntityIds.length > 0) {
      return subEntityIds.map(id => ({ id }) as GetEntityQuery);
    }

    const parentEntity = await this.entityService.getEntity(entityId, true);
    const isRootEntity = parentEntity?.entityType === EntityTypeEnum.ROOT_ENTITY;
    const subEntities = await this.entityService.getSubEntitiesList(entityId, isRootEntity, false, parentEntity);

    if (subEntities?.length) {
      return subEntities.filter((entity): entity is GetEntityQuery => Boolean(entity?.id));
    }

    return this.resolveSubEntityIds(this.collectionService.getCurrentUser()).map(id => ({ id }) as GetEntityQuery);
  }

  private getSubEntityFilter(
    isParticipant: boolean,
    isSubEntityLeader: boolean,
    currentUser: any,
    subEntityId: string
  ): Partial<ModelArtifactsRegistryFilterInput> {
    if (!subEntityId) {
      return {};
    }

    if (isParticipant && currentUser?.id) {
      const artifactSubEntityIds = this.resolveSubEntityIds(currentUser);
      const subEntityInArtifactList = artifactSubEntityIds.includes(subEntityId);
      const otherArtifactSubEntityIds = artifactSubEntityIds.filter(id => id !== subEntityId);

      if (subEntityInArtifactList) {
        const allIds = [subEntityId, ...otherArtifactSubEntityIds];
        return allIds.length > 1
          ? { or: allIds.map(id => ({ subEntityId: { eq: id } })) }
          : { subEntityId: { eq: subEntityId } };
      }

      const orConditions: ModelArtifactsRegistryFilterInput[] = [
        { subEntityId: { eq: subEntityId }, creatorId: { eq: currentUser.id } },
        ...otherArtifactSubEntityIds.map(id => ({ subEntityId: { eq: id } })),
      ];
      return orConditions.length > 1
        ? { or: orConditions }
        : { subEntityId: { eq: subEntityId }, creatorId: { eq: currentUser.id } };
    }

    if (isSubEntityLeader) {
      const artifactSubEntityIds = this.resolveSubEntityIds(currentUser);
      return artifactSubEntityIds.length > 1
        ? { or: artifactSubEntityIds.map(id => ({ subEntityId: { eq: id } })) }
        : { subEntityId: { eq: artifactSubEntityIds[0] ?? subEntityId } };
    }

    return { subEntityId: { eq: subEntityId } };
  }

  private mapArtifacts(
    allApiItems: any[],
    currentUser: any,
    isParticipant: boolean,
    isSubEntityLeader: boolean,
    subEntityId: string
  ): CollectionUploadArtifactItem[] {
    const artifactSubEntityIdSet = new Set<string>(this.resolveSubEntityIds(currentUser));

    return allApiItems
      .filter(item => {
        if (item.status === ArtifactStatus.ARCHIVED) {
          return false;
        }

        if (isParticipant && subEntityId) {
          if (artifactSubEntityIdSet.has(item.subEntityId)) {
            return true;
          }

          if (item.subEntityId === subEntityId) {
            return item.creatorId === currentUser?.id;
          }

          return false;
        }

        if (isSubEntityLeader && artifactSubEntityIdSet.size > 0) {
          return artifactSubEntityIdSet.has(item.subEntityId);
        }

        if (subEntityId && item.subEntityId !== subEntityId) {
          return false;
        }

        return true;
      })
      .map(item => {
        const creatorName = item.creator?.name || 'Unknown User';
        const formattedDate = this.formatArtifactDate(item.createdAt);

        return {
          id: item.parentID,
          name: item.name || '',
          uploadedBy: creatorName,
          uploadedDate: formattedDate,
          createdBy: creatorName,
          date: formattedDate,
          createdAt: item.createdAt || '',
          creatorId: item.creatorId,
        };
      });
  }

  private resolveSubEntityIds(user: any): string[] {
    const ids = user?.artifactSubEntities?.length ? user.artifactSubEntities : (user?.entityIds ?? []);
    return (ids as any[]).filter((id): id is string => !!id);
  }

  private formatArtifactDate(createdAt: string | undefined): string {
    if (!createdAt) {
      return '';
    }

    return new Date(createdAt).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
}
